import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Edit, RefreshCw, Archive, Trash2, Calendar, Upload, Eye, FileText, X } from "lucide-react";
import Layout from "../components/Layout";
import { TerminarContratoDialog } from "../components/propiedad/AccionesRapidas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getStatusBadgeClass = (status) => {
  const classes = {
    "Activo": "badge-activo",
    "Vencido": "badge-vencido",
    "Próximo a vencer": "badge-proximo",
    "Por comenzar": "badge-por-comenzar",
    "Renovado": "badge-renovado",
    "Archivado": "badge-archivado",
    "Terminado": "badge-archivado"
  };
  return `badge ${classes[status] || ""}`;
};

const getCargoStatusBadgeClass = (estado) => {
  const classes = {
    "Pagado": "badge-activo",
    "Pendiente": "badge-por-comenzar",
    "Vencido": "badge-vencido",
    "Parcial": "badge-proximo"
  };
  return `badge ${classes[estado] || ""}`;
};

const formatCurrency = (amount, currency = "MXN") => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

const formatDate = (dateString) => {
  // Parse date string without timezone conversion
  const [year, month, day] = dateString.split('T')[0].split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export default function ContractDetail({ user, onLogout }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRenewDialog, setShowRenewDialog] = useState(false);
  const [showTerminarDialog, setShowTerminarDialog] = useState(false);
  const [renewData, setRenewData] = useState({
    fecha_inicio: "",
    fecha_termino: "",
    renta_mensual: "",
    deposito_garantia: "",
    observaciones: ""
  });
  const [renewLoading, setRenewLoading] = useState(false);
  
  // File upload states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitulo, setUploadTitulo] = useState("");
  const [uploadLoading, setUploadLoading] = useState(false);
  
  // Cobranza states
  const [cargos, setCargos] = useState([]);
  const [cargosLoading, setCargosLoading] = useState(true);
  const [cobranzaStats, setCobranzaStats] = useState({
    total: 0,
    pagado: 0,
    pendiente: 0,
    vencido: 0
  });

  useEffect(() => {
    fetchContract();
    fetchCargos();
    if (searchParams.get('action') === 'renew') {
      setShowRenewDialog(true);
    }
  }, [id]);

  const fetchContract = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/contracts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContract(response.data);
      setRenewData({
        fecha_inicio: "",
        fecha_termino: "",
        renta_mensual: response.data.renta_mensual,
        deposito_garantia: response.data.deposito_garantia,
        observaciones: ""
      });
    } catch (error) {
      toast.error("Error al cargar el contrato");
      navigate("/contratos");
    } finally {
      setLoading(false);
    }
  };

  const fetchCargos = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/cobranza/cargos/contrato/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const cargosList = response.data;
      
      // Sort cargos by fecha_vencimiento (oldest to newest)
      const sortedCargos = cargosList.sort((a, b) => {
        const dateA = new Date(a.fecha_vencimiento);
        const dateB = new Date(b.fecha_vencimiento);
        return dateA - dateB;
      });
      
      setCargos(sortedCargos);
      
      // Calculate stats
      const stats = cargosList.reduce((acc, cargo) => {
        acc.total += cargo.importe;
        
        if (cargo.estado === "Pagado") {
          acc.pagado += cargo.importe;
        } else if (cargo.estado === "Vencido") {
          acc.vencido += cargo.saldo_pendiente;
          acc.pendiente += cargo.saldo_pendiente;
        } else if (cargo.estado === "Pendiente") {
          acc.pendiente += cargo.saldo_pendiente;
        } else if (cargo.estado === "Parcial") {
          acc.pagado += (cargo.importe - cargo.saldo_pendiente);
          acc.pendiente += cargo.saldo_pendiente;
        }
        
        return acc;
      }, { total: 0, pagado: 0, pendiente: 0, vencido: 0 });
      
      setCobranzaStats(stats);
    } catch (error) {
      console.error("Error al cargar cargos:", error);
    } finally {
      setCargosLoading(false);
    }
  };

  const handleRenew = async () => {
    setRenewLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        fecha_inicio: renewData.fecha_inicio,
        fecha_termino: renewData.fecha_termino,
        renta_mensual: parseFloat(renewData.renta_mensual),
        deposito_garantia: parseFloat(renewData.deposito_garantia),
        observaciones: renewData.observaciones || null
      };

      const response = await axios.post(`${API}/contracts/${id}/renew`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Contrato renovado exitosamente");
      setShowRenewDialog(false);
      navigate(`/contratos/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al renovar el contrato");
    } finally {
      setRenewLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm("¿Estás seguro de archivar este contrato?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/contracts/${id}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contrato archivado");
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al archivar");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("¿Estás seguro de eliminar este contrato? Esta acción no se puede deshacer.")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/contracts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contrato eliminado");
      navigate("/contratos");
    } catch (error) {
      toast.error("Error al eliminar el contrato");
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile || !uploadTitulo.trim()) {
      toast.error("Por favor selecciona un archivo y proporciona un título");
      return;
    }

    if (contract.archivos && contract.archivos.length >= 8) {
      toast.error("Se ha alcanzado el límite de 8 archivos por contrato");
      return;
    }

    setUploadLoading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("titulo", uploadTitulo);

      await axios.post(`${API}/contracts/${id}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Archivo subido exitosamente");
      setShowUploadDialog(false);
      setUploadFile(null);
      setUploadTitulo("");
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir el archivo");
    } finally {
      setUploadLoading(false);
    }
  };

  const handleFilePreview = async (fileId, fileName) => {
    try {
      const token = localStorage.getItem("token");
      // Open file in new tab for preview
      const fileUrl = `${API}/contracts/${id}/files/${fileId}?token=${token}`;
      window.open(fileUrl, '_blank');
    } catch (error) {
      toast.error("Error al abrir el archivo");
    }
  };

  const handleFileDelete = async (fileId) => {
    if (!window.confirm("¿Estás seguro de eliminar este archivo?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/contracts/${id}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Archivo eliminado");
      fetchContract();
    } catch (error) {
      toast.error("Error al eliminar el archivo");
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Cargando...</div>
        </div>
      </Layout>
    );
  }

  if (!contract) return null;

  const esAdmin = user.rol === "admin" || user.rol === "maestro";
  const canEdit = esAdmin && !["Archivado", "Renovado", "Terminado"].includes(contract.estatus);
  const canRenew = esAdmin && ["Activo", "Vencido", "Próximo a vencer"].includes(contract.estatus);
  const canArchive = esAdmin && ["Vencido", "Renovado", "Terminado"].includes(contract.estatus);
  const canTerminate = esAdmin && ["Activo", "Vencido", "Próximo a vencer", "Por comenzar"].includes(contract.estatus);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold text-gray-900">
                Contrato {contract.numero_contrato}
              </h1>
              <span className={getStatusBadgeClass(contract.estatus)} data-testid="contract-status">
                {contract.estatus}
              </span>
            </div>
            <p className="text-gray-500 mt-1">{contract.inmueble_nombre}</p>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/contratos")} data-testid="back-button">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            {canEdit && (
              <Button onClick={() => navigate(`/contratos/editar/${id}`)} data-testid="edit-contract-button">
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            )}
            {canRenew && (
              <Button onClick={() => setShowRenewDialog(true)} data-testid="renew-contract-button">
                <RefreshCw className="w-4 h-4 mr-2" />
                Renovar
              </Button>
            )}
            {canTerminate && (
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setShowTerminarDialog(true)}
                data-testid="terminar-contract-button"
              >
                <X className="w-4 h-4 mr-2" />
                Terminar / Desocupar
              </Button>
            )}
            {canArchive && (
              <Button variant="outline" onClick={handleArchive} data-testid="archive-contract-button">
                <Archive className="w-4 h-4 mr-2" />
                Archivar
              </Button>
            )}
            {(user.rol === "admin" || user.rol === "maestro") && (
              <Button variant="destructive" onClick={handleDelete} data-testid="delete-contract-button">
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Contract info in cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Propietario */}
          <Card>
            <CardHeader>
              <CardTitle>Propietario</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Nombre</p>
                <p className="font-medium" data-testid="propietario-nombre">{contract.propietario_nombre}</p>
              </div>
              {contract.propietario_rfc && (
                <div>
                  <p className="text-sm text-gray-500">RFC</p>
                  <p className="font-medium">{contract.propietario_rfc}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Teléfono</p>
                <p className="font-medium">{contract.propietario_telefono}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{contract.propietario_email}</p>
              </div>
              {contract.propietario_relacion && (
                <div>
                  <p className="text-sm text-gray-500">Relación</p>
                  <p className="font-medium">{contract.propietario_relacion}</p>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/propietarios/${encodeURIComponent(contract.propietario_email)}`)}
                data-testid="view-owner-button"
              >
                Ver todos sus contratos
              </Button>
            </CardContent>
          </Card>

          {/* Inquilino */}
          <Card>
            <CardHeader>
              <CardTitle>Inquilino</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Nombre</p>
                <p className="font-medium" data-testid="inquilino-nombre">{contract.inquilino_nombre}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">RFC</p>
                <p className="font-medium">{contract.inquilino_rfc}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Teléfono</p>
                <p className="font-medium">{contract.inquilino_telefono}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{contract.inquilino_email}</p>
              </div>
              {contract.inquilino_tipo && (
                <div>
                  <p className="text-sm text-gray-500">Tipo</p>
                  <p className="font-medium">{contract.inquilino_tipo}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Inmueble */}
          <Card>
            <CardHeader>
              <CardTitle>Inmueble</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Nombre</p>
                <p className="font-medium" data-testid="inmueble-nombre">{contract.inmueble_nombre}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Dirección</p>
                <p className="font-medium">{contract.inmueble_direccion}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Tipo</p>
                <p className="font-medium">{contract.inmueble_tipo}</p>
              </div>
              {contract.inmueble_superficie && (
                <div>
                  <p className="text-sm text-gray-500">Superficie</p>
                  <p className="font-medium">{contract.inmueble_superficie} m²</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Condiciones Económicas */}
          <Card>
            <CardHeader>
              <CardTitle>Condiciones Económicas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Tipo de Contrato</p>
                <p className="font-medium">{contract.tipo_contrato || "Simple"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Renta Mensual</p>
                <p className="font-medium text-lg" data-testid="renta-mensual">
                  {formatCurrency(contract.renta_mensual, contract.moneda)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Depósito en Garantía</p>
                <p className="font-medium">{formatCurrency(contract.deposito_garantia, contract.moneda)}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Fecha Inicio</p>
                  <p className="font-medium">{formatDate(contract.fecha_inicio)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fecha Término</p>
                  <p className="font-medium">{formatDate(contract.fecha_termino)}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Día de Pago</p>
                <p className="font-medium">Día {contract.dia_pago} de cada mes</p>
              </div>
              {contract.incremento_anual && (
                <div>
                  <p className="text-sm text-gray-500">Incremento Anual</p>
                  <p className="font-medium">
                    {contract.incremento_porcentaje}% anual
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Fiadores */}
        {contract.fiadores && contract.fiadores.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Fiadores</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {contract.fiadores.map((fiador, index) => (
                  <div key={index} className="border rounded-lg p-4" data-testid={`fiador-${index}`}>
                    <h4 className="font-medium mb-2">Fiador {index + 1}</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Nombre</p>
                        <p className="font-medium">{fiador.nombre_completo}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Teléfono</p>
                        <p className="font-medium">{fiador.telefono}</p>
                      </div>
                      {fiador.email && (
                        <div>
                          <p className="text-gray-500">Email</p>
                          <p className="font-medium">{fiador.email}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-gray-500">Tipo de Garantía</p>
                        <p className="font-medium">{fiador.tipo_garantia}</p>
                      </div>
                      {fiador.observaciones && (
                        <div className="col-span-2">
                          <p className="text-gray-500">Observaciones</p>
                          <p className="font-medium">{fiador.observaciones}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Información Adicional */}
        <Card>
          <CardHeader>
            <CardTitle>Información Adicional</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {contract.fecha_elaboracion && (
              <div>
                <p className="text-sm text-gray-500">Fecha de Elaboración</p>
                <p className="font-medium">{formatDate(contract.fecha_elaboracion)}</p>
              </div>
            )}
            {contract.responsable_interno && (
              <div>
                <p className="text-sm text-gray-500">Responsable Interno</p>
                <p className="font-medium">{contract.responsable_interno}</p>
              </div>
            )}
            {contract.observaciones && (
              <div>
                <p className="text-sm text-gray-500">Observaciones</p>
                <p className="font-medium">{contract.observaciones}</p>
              </div>
            )}
            {contract.contrato_origen_id && (
              <div>
                <p className="text-sm text-gray-500">Este contrato es una renovación de</p>
                <Button
                  variant="link"
                  className="p-0 h-auto"
                  onClick={() => navigate(`/contratos/${contract.contrato_origen_id}`)}
                >
                  Ver contrato anterior
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Archivos Adjuntos */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Archivos Adjuntos</CardTitle>
              {(user.rol === "admin" || user.rol === "maestro") && canEdit && (
                <Button 
                  onClick={() => setShowUploadDialog(true)} 
                  size="sm"
                  disabled={contract.archivos && contract.archivos.length >= 8}
                  data-testid="upload-file-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Subir Archivo
                </Button>
              )}
            </div>
            {contract.archivos && contract.archivos.length > 0 && (
              <p className="text-sm text-gray-500 mt-1">
                {contract.archivos.length} de 8 archivos
              </p>
            )}
          </CardHeader>
          <CardContent>
            {!contract.archivos || contract.archivos.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No hay archivos adjuntos</p>
            ) : (
              <div className="space-y-3">
                {contract.archivos.map((archivo) => (
                  <div 
                    key={archivo.id} 
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    data-testid={`file-${archivo.id}`}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <FileText className="w-8 h-8 text-blue-500" />
                      <div className="flex-1">
                        <p className="font-medium">{archivo.titulo}</p>
                        <p className="text-sm text-gray-500">
                          {archivo.nombre_archivo} • {(archivo.tamano / 1024).toFixed(2)} KB
                        </p>
                        <p className="text-xs text-gray-400">
                          Subido: {formatDate(archivo.fecha_subida)}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleFilePreview(archivo.id, archivo.nombre_archivo)}
                        data-testid={`preview-file-${archivo.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {(user.rol === "admin" || user.rol === "maestro") && canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleFileDelete(archivo.id)}
                          data-testid={`delete-file-${archivo.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cobranza Section */}
        <Card>
          <CardHeader>
            <CardTitle>Cobranza del Contrato</CardTitle>
          </CardHeader>
          <CardContent>
            {cargosLoading ? (
              <p className="text-gray-500 text-center py-8">Cargando información de cobranza...</p>
            ) : cargos.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No hay cargos generados para este contrato</p>
            ) : (
              <div className="space-y-6">
                {/* Stats Summary */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-600 font-medium">Total a Cobrar</p>
                    <p className="text-2xl font-bold text-blue-900 mt-1">
                      {formatCurrency(cobranzaStats.total, contract.moneda)}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-green-600 font-medium">Pagado</p>
                    <p className="text-2xl font-bold text-green-900 mt-1">
                      {formatCurrency(cobranzaStats.pagado, contract.moneda)}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <p className="text-sm text-orange-600 font-medium">Pendiente</p>
                    <p className="text-2xl font-bold text-orange-900 mt-1">
                      {formatCurrency(cobranzaStats.pendiente, contract.moneda)}
                    </p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-red-600 font-medium">Vencido</p>
                    <p className="text-2xl font-bold text-red-900 mt-1">
                      {formatCurrency(cobranzaStats.vencido, contract.moneda)}
                    </p>
                  </div>
                </div>

                {/* Cargos Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Descripción
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vencimiento
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Importe
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Pagado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Saldo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {cargos.map((cargo) => (
                        <tr key={cargo.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {cargo.tipo}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {cargo.descripcion}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {formatDate(cargo.fecha_vencimiento)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {formatCurrency(cargo.importe, contract.moneda)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                            {formatCurrency(cargo.importe - cargo.saldo_pendiente, contract.moneda)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {formatCurrency(cargo.saldo_pendiente, contract.moneda)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={getCargoStatusBadgeClass(cargo.estado)}>
                              {cargo.estado}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upload File Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Subir Archivo</DialogTitle>
            <DialogDescription>
              Selecciona un archivo y proporciona un título descriptivo (máximo 8 archivos por contrato)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="file-titulo">Título del archivo *</Label>
              <Input
                id="file-titulo"
                data-testid="file-titulo"
                value={uploadTitulo}
                onChange={(e) => setUploadTitulo(e.target.value)}
                placeholder="Ej: Contrato firmado"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="file-upload">Archivo *</Label>
              <Input
                id="file-upload"
                type="file"
                data-testid="file-upload"
                onChange={(e) => setUploadFile(e.target.files[0])}
              />
              {uploadFile && (
                <p className="text-sm text-gray-500">
                  {uploadFile.name} ({(uploadFile.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadDialog(false);
                setUploadFile(null);
                setUploadTitulo("");
              }}
              data-testid="upload-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleFileUpload}
              disabled={uploadLoading || !uploadFile || !uploadTitulo.trim()}
              data-testid="upload-confirm-button"
            >
              {uploadLoading ? "Subiendo..." : "Subir Archivo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Terminar / Desocupar Dialog */}
      <TerminarContratoDialog
        open={showTerminarDialog}
        onOpenChange={setShowTerminarDialog}
        contratoId={id}
        contratoNumero={contract.numero_contrato}
        onSuccess={fetchContract}
      />

      {/* Renew Dialog */}
      <Dialog open={showRenewDialog} onOpenChange={setShowRenewDialog}>
        <DialogContent data-testid="renew-dialog">
          <DialogHeader>
            <DialogTitle>Renovar Contrato</DialogTitle>
            <DialogDescription>
              Ingresa las nuevas fechas y condiciones para la renovación del contrato.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="renew-fecha-inicio">Fecha de Inicio *</Label>
              <Input
                id="renew-fecha-inicio"
                type="date"
                data-testid="renew-fecha-inicio"
                value={renewData.fecha_inicio}
                onChange={(e) => setRenewData({ ...renewData, fecha_inicio: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="renew-fecha-termino">Fecha de Término *</Label>
              <Input
                id="renew-fecha-termino"
                type="date"
                data-testid="renew-fecha-termino"
                value={renewData.fecha_termino}
                onChange={(e) => setRenewData({ ...renewData, fecha_termino: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="renew-renta">Renta Mensual *</Label>
              <Input
                id="renew-renta"
                type="number"
                step="0.01"
                data-testid="renew-renta"
                value={renewData.renta_mensual}
                onChange={(e) => setRenewData({ ...renewData, renta_mensual: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="renew-deposito">Depósito en Garantía *</Label>
              <Input
                id="renew-deposito"
                type="number"
                step="0.01"
                data-testid="renew-deposito"
                value={renewData.deposito_garantia}
                onChange={(e) => setRenewData({ ...renewData, deposito_garantia: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="renew-observaciones">Observaciones</Label>
              <Textarea
                id="renew-observaciones"
                data-testid="renew-observaciones"
                value={renewData.observaciones}
                onChange={(e) => setRenewData({ ...renewData, observaciones: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRenewDialog(false)}
              data-testid="renew-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRenew}
              disabled={renewLoading || !renewData.fecha_inicio || !renewData.fecha_termino}
              data-testid="renew-confirm-button"
            >
              {renewLoading ? "Renovando..." : "Renovar Contrato"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
